import React, { useEffect, useRef, useState } from "react";
import {
  Button,
  Card,
  Col,
  Form,
  Input,
  Row,
  Select,
  Space,
  Spin,
  Typography,
  Upload,
  message,
} from "antd";
import update from "immutability-helper";
import { DndProvider, useDrag, useDrop } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import {
  ArrowLeftOutlined,
  DeleteOutlined,
  HolderOutlined,
  PlusOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import { useNavigate, useParams } from "react-router-dom";
import { API_BASE_URL } from "../api/api";

const { Title, Text } = Typography;
const { TextArea } = Input;

const TYPE_OPTIONS = [
  { label: "Country", value: "country" },
  { label: "City", value: "city" },
];

const STATUS_OPTIONS = [
  { label: "Draft", value: "draft" },
  { label: "Published", value: "published" },
  { label: "Archived", value: "archived" },
];

const LOCALE_OPTIONS = [
  { label: "English", value: "en" },
  { label: "Traditional Chinese", value: "zh-TW" },
  { label: "Simplified Chinese", value: "zh-CN" },
];

const TAB_TYPE_OPTIONS = [
  { label: "Plain Text", value: "plain_text" },
  { label: "Structured List", value: "structured_list" },
];

const DRAGGABLE_CHILD_DESTINATION_TYPE = "destination-child-row";
const DESTINATION_IMAGE_UPLOAD_ENDPOINT = `${API_BASE_URL}destinationImageUpload`;

const sanitizeJsonString = (value) => {
  let sanitized = "";
  let inString = false;
  let isEscaped = false;

  for (let index = 0; index < value.length; index += 1) {
    const character = value[index];

    if (isEscaped) {
      sanitized += character;
      isEscaped = false;
      continue;
    }

    if (character === "\\") {
      sanitized += character;
      isEscaped = true;
      continue;
    }

    if (character === '"') {
      sanitized += character;
      inString = !inString;
      continue;
    }

    if (inString) {
      if (character === "\n") {
        sanitized += "\\n";
        continue;
      }

      if (character === "\r") {
        sanitized += "\\r";
        continue;
      }

      if (character === "\t") {
        sanitized += "\\t";
        continue;
      }
    }

    sanitized += character;
  }

  return sanitized;
};

const parseJsonValue = (value, fallback) => {
  if (value === null || value === undefined || value === "") {
    return fallback;
  }

  if (typeof value === "string") {
    try {
      return JSON.parse(value);
    } catch (error) {
      try {
        return JSON.parse(sanitizeJsonString(value));
      } catch (sanitizedError) {
        return fallback;
      }
    }
  }

  return value;
};

const parseJsonArray = (value) => {
  const parsed = parseJsonValue(value, []);
  return Array.isArray(parsed) ? parsed : [];
};

const buildEmptyImage = () => ({
  url: "",
  alt: "",
});

const buildEmptyHotel = () => ({
  hotel_id: null,
  hotel_name: "",
  cover_image_url: "",
});

const buildEmptyStructuredListItem = () => ({
  label: "",
  value: "",
});

const buildEmptyTab = () => ({
  label: "",
  type: "plain_text",
  content_text: "",
  content_items: [],
});

const buildEmptyThingToDo = () => ({
  title: "",
  description: "",
  image_url: "",
});

const buildEmptyFaq = () => ({
  question: "",
  answer: "",
});

const normalizeTabForForm = (tab) => {
  const content = tab?.content;
  const tabType =
    tab?.type === "structured_list" || Array.isArray(content)
      ? "structured_list"
      : "plain_text";

  return {
    label: tab?.label || "",
    type: tabType,
    content_text:
      tabType === "plain_text"
        ? typeof content === "string"
          ? content
          : content === null || content === undefined
            ? ""
            : JSON.stringify(content)
        : "",
    content_items:
      tabType === "structured_list" && Array.isArray(content) && content.length > 0
        ? content.map((item) => ({
            label: item?.label || "",
            value: item?.value || "",
          }))
        : [],
  };
};

const normalizeThingToDoForForm = (item) => ({
  title: item?.title || "",
  description: item?.description || "",
  image_url: item?.image_url || item?.image || "",
});

const normalizeFaqForForm = (item) => ({
  question: item?.question || "",
  answer: item?.answer || "",
});

const buildEmptyTranslation = (locale = "en") => ({
  locale,
  name: "",
  page_title: "",
  meta_description: "",
  hero_title: "",
  hero_subtitle: "",
  parent_card_intro: "",
  tabs: [],
  section_title: "",
  things_to_do: [],
  packages_title: "",
  packages_description: "",
  featured_resorts_title: "",
  faq_title: "",
  faqs: [],
});

const ImageUrlPreview = ({ url, alt = "Preview", height = 140 }) => {
  const normalizedUrl = (url || "").trim();

  if (!normalizedUrl) {
    return null;
  }

  return (
    <div style={{ marginTop: 8 }}>
      <Text type="secondary" style={{ display: "block", marginBottom: 8 }}>
        Preview
      </Text>
      <div
        style={{
          border: "1px solid #f0f0f0",
          borderRadius: 8,
          padding: 8,
          background: "#fafafa",
          display: "inline-block",
        }}
      >
        <img
          src={normalizedUrl}
          alt={alt}
          style={{
            width: 220,
            maxWidth: "100%",
            height,
            objectFit: "cover",
            borderRadius: 6,
            display: "block",
          }}
        />
      </div>
    </div>
  );
};

const getUploadFile = (file) => {
  if (file?.originFileObj instanceof File) {
    return file.originFileObj;
  }

  if (file instanceof File) {
    return file;
  }

  return null;
};

const DestinationImageField = ({
  form,
  name,
  label,
  token,
  category,
  previewAlt,
  previewHeight = 140,
  rules = [],
}) => {
  const [uploading, setUploading] = useState(false);
  const currentValue = Form.useWatch(name, form);

  const handleUploadChange = async (info) => {
    const file = getUploadFile(info.file);

    if (!file) {
      message.error("Invalid file selected");
      return;
    }

    const formData = new FormData();
    formData.append("category", category);
    formData.append("image", file);

    const hide = message.loading("Uploading image...", 0);
    setUploading(true);

    try {
      const res = await fetch(DESTINATION_IMAGE_UPLOAD_ENDPOINT, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      });
      const rawResponse = await res.text();
      let result = null;

      try {
        result = rawResponse ? JSON.parse(rawResponse) : null;
      } catch (parseError) {
        throw new Error("Upload endpoint returned an invalid response");
      }

      if (!res.ok || !result || !result.success || !result.url) {
        throw new Error(result?.error || result?.message || "Upload failed");
      }

      form.setFieldValue(name, result.url);
      message.success("Image uploaded");
    } catch (error) {
      console.error(error);
      message.error(error.message || "Upload failed");
    } finally {
      hide();
      setUploading(false);
    }
  };

  return (
    <>
      <Form.Item label={label} required={rules.some((rule) => rule.required)}>
        <Form.Item name={name} noStyle rules={rules}>
          <Input type="hidden" />
        </Form.Item>
        <Upload
          beforeUpload={() => false}
          showUploadList={false}
          accept="image/*"
          maxCount={1}
          onChange={handleUploadChange}
        >
          <Button icon={<UploadOutlined />} loading={uploading}>
            Upload Image
          </Button>
        </Upload>
      </Form.Item>
      <ImageUrlPreview
        url={currentValue}
        alt={previewAlt}
        height={previewHeight}
      />
    </>
  );
};

const AddActionButton = ({ children, onClick, block = false }) => (
  <Button
    type="dashed"
    icon={<PlusOutlined />}
    onClick={onClick}
    block={block}
    style={{
      borderColor: "#52c41a",
      color: "#389e0d",
      background: "#f6ffed",
      fontWeight: 600,
    }}
  >
    {children}
  </Button>
);

const DraggableChildDestinationRow = ({
  index,
  moveChildDestination,
  children,
}) => {
  const containerRef = useRef(null);
  const handleRef = useRef(null);

  const [{ isOver }, drop] = useDrop({
    accept: DRAGGABLE_CHILD_DESTINATION_TYPE,
    collect: (monitor) => ({ isOver: monitor.isOver({ shallow: true }) }),
    hover(item) {
      if (!containerRef.current) {
        return;
      }

      const dragIndex = item.index;
      const hoverIndex = index;

      if (dragIndex === hoverIndex) {
        return;
      }

      moveChildDestination(dragIndex, hoverIndex);
      item.index = hoverIndex;
    },
  });

  const [{ isDragging }, drag] = useDrag({
    type: DRAGGABLE_CHILD_DESTINATION_TYPE,
    item: { index },
    collect: (monitor) => ({ isDragging: monitor.isDragging() }),
  });

  drop(containerRef);
  drag(handleRef);

  return (
    <div
      ref={containerRef}
      style={{
        marginBottom: 12,
        padding: 12,
        border: "1px solid #f0f0f0",
        borderRadius: 8,
        background: isDragging ? "#fafafa" : isOver ? "#f6ffed" : "#fff",
        opacity: isDragging ? 0.7 : 1,
      }}
    >
      {children(handleRef)}
    </div>
  );
};

const TranslationTabEditor = ({
  form,
  translationIndex,
  tabField,
  removeTab,
}) => {
  const tabType =
    Form.useWatch(
      ["translations", translationIndex, "tabs", tabField.name, "type"],
      form
    ) || form.getFieldValue(["translations", translationIndex, "tabs", tabField.name, "type"]);

  return (
    <Card
      key={tabField.key}
      type="inner"
      size="small"
      style={{ marginBottom: 12 }}
      extra={
        <Button
          danger
          type="text"
          icon={<DeleteOutlined />}
          onClick={() => removeTab(tabField.name)}
        />
      }
    >
      <Row gutter={12}>
        <Col xs={24} md={10}>
          <Form.Item
            label="Tab Label"
            name={[tabField.name, "label"]}
            rules={[
              {
                required: true,
                message: "Please enter a tab label",
              },
            ]}
          >
            <Input />
          </Form.Item>
        </Col>
        <Col xs={24} md={10}>
          <Form.Item
            label="Tab Type"
            name={[tabField.name, "type"]}
            rules={[
              {
                required: true,
                message: "Please select a tab type",
              },
            ]}
          >
            <Select options={TAB_TYPE_OPTIONS} />
          </Form.Item>
        </Col>
      </Row>

      {tabType === "structured_list" ? (
        <Form.List name={[tabField.name, "content_items"]}>
          {(contentFields, { add: addContentItem, remove: removeContentItem }) => (
            <Card type="inner" title="Structured List Items">
              {contentFields.map((contentField) => (
                <Row gutter={12} key={contentField.key} align="middle">
                  <Col xs={24} md={10}>
                    <Form.Item
                      label="Label"
                      name={[contentField.name, "label"]}
                      rules={[
                        {
                          required: true,
                          message: "Please enter a label",
                        },
                      ]}
                    >
                      <Input />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={10}>
                    <Form.Item
                      label="Value"
                      name={[contentField.name, "value"]}
                      rules={[
                        {
                          required: true,
                          message: "Please enter a value",
                        },
                      ]}
                    >
                      <Input />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={2}>
                    <Button
                      danger
                      type="text"
                      icon={<DeleteOutlined />}
                      onClick={() => removeContentItem(contentField.name)}
                      style={{ marginTop: 30 }}
                    />
                  </Col>
                </Row>
              ))}
              <AddActionButton onClick={() => addContentItem(buildEmptyStructuredListItem())}>
                Add Item
              </AddActionButton>
            </Card>
          )}
        </Form.List>
      ) : (
        <Form.Item
          label="Tab Content"
          name={[tabField.name, "content_text"]}
          rules={[
            {
              required: true,
              message: "Please enter tab content",
            },
          ]}
        >
          <TextArea rows={4} />
        </Form.Item>
      )}
    </Card>
  );
};

const buildChildDestinationsForCountry = (record, allDestinations) => {
  return allDestinations
    .filter((item) => Number(item.parent_id) === Number(record.id))
    .sort((a, b) => {
      const sortA = Number(a.child_sort_order || 0);
      const sortB = Number(b.child_sort_order || 0);

      if (sortA !== sortB) {
        return sortA - sortB;
      }

      return Number(a.id) - Number(b.id);
    })
    .map((item) => {
      return {
        destination_id: Number(item.id),
        destination_name: item.display_name || `#${item.id}`,
        hero_background_image_url: item.hero_background_image_url || "",
        child_sort_order: Number(item.child_sort_order || 0),
      };
    });
};

const toFormValues = (record, allDestinations = []) => ({
  id: record.id,
  type: record.type || "country",
  status: record.status || "draft",
  parent_id: record.parent_id ? Number(record.parent_id) : null,
  slug:
    record.slug ||
    record.display_slug ||
    record.translations?.find((translation) => translation.locale === "en")?.slug ||
    record.translations?.[0]?.slug ||
    "",
  hero_background_image_url: record.hero_background_image_url || "",
  destination_images: parseJsonArray(record.destination_images_json).map(
    (item) => ({
      url: item?.url || "",
      alt: item?.alt || "",
    })
  ),
  child_destinations:
    record.type === "country"
      ? buildChildDestinationsForCountry(record, allDestinations)
      : [],
  recommended_hotels: parseJsonArray(record.recommended_hotels_json).map(
    (item) => ({
      hotel_id:
        item?.hotel_id === null || item?.hotel_id === undefined
          ? null
          : String(item.hotel_id),
      hotel_name: item?.hotel_name || "",
      cover_image_url: item?.cover_image_url || "",
    })
  ),
  translations:
    record.translations?.length > 0
      ? record.translations.map((translation) => ({
          locale: translation.locale || "",
          name: translation.name || "",
          page_title: translation.page_title || "",
          meta_description: translation.meta_description || "",
          hero_title: translation.hero_title || "",
          hero_subtitle: translation.hero_subtitle || "",
          parent_card_intro: translation.parent_card_intro || "",
          tabs: parseJsonArray(translation.tabs_json).map(normalizeTabForForm),
          section_title: translation.section_title || "",
          things_to_do: parseJsonArray(translation.things_to_do_json).map(
            normalizeThingToDoForForm
          ),
          packages_title: translation.packages_title || "",
          packages_description: translation.packages_description || "",
          featured_resorts_title: translation.featured_resorts_title || "",
          faq_title: translation.faq_title || "",
          faqs: parseJsonArray(translation.faqs_json).map(normalizeFaqForForm),
        }))
      : [buildEmptyTranslation()],
});

const DestinationEditorPage = () => {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const { destinationId } = useParams();
  const token = localStorage.getItem("token");
  const isEditing = Boolean(destinationId);

  const [saving, setSaving] = useState(false);
  const [pageLoading, setPageLoading] = useState(false);
  const [destinations, setDestinations] = useState([]);
  const [hotelSearchOptions, setHotelSearchOptions] = useState([]);
  const [hotelSearchLoading, setHotelSearchLoading] = useState(false);
  const [hotelSearchQuery, setHotelSearchQuery] = useState("");
  const [hotelSearchError, setHotelSearchError] = useState("");

  const hotelSearchTimeoutRef = useRef(null);
  const latestHotelSearchRef = useRef("");

  const selectedType = Form.useWatch("type", form);

  useEffect(() => {
    return () => {
      if (hotelSearchTimeoutRef.current) {
        clearTimeout(hotelSearchTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (selectedType === "country") {
      form.setFieldValue("parent_id", null);
      const currentTranslations = form.getFieldValue("translations") || [];
      form.setFieldValue(
        "translations",
        currentTranslations.map((translation) => ({
          ...translation,
          things_to_do: [],
        }))
      );
    }

    if (selectedType === "city") {
      form.setFieldValue("child_destinations", []);
    }
  }, [selectedType, form]);

  useEffect(() => {
    const loadData = async () => {
      setPageLoading(true);
      try {
        const res = await fetch(`${API_BASE_URL}destinations`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        const result = await res.json();

        if (!res.ok || !result.success) {
          message.error(result.error || "Failed to load destinations");
          return;
        }

        const rows = Array.isArray(result.data) ? result.data : [];
        setDestinations(rows);

        if (isEditing) {
          const current = rows.find(
            (item) => String(item.id) === String(destinationId)
          );

          if (!current) {
            message.error("Destination not found");
            navigate("/destinations");
            return;
          }

          form.setFieldsValue(toFormValues(current, rows));
        } else {
          form.setFieldsValue({
            type: "country",
            status: "draft",
            slug: "",
            destination_images: [],
            child_destinations: [],
            recommended_hotels: [],
            translations: [buildEmptyTranslation()],
          });
        }
      } catch (error) {
        console.error(error);
        message.error("Failed to load destination data");
      } finally {
        setPageLoading(false);
      }
    };

    loadData();
  }, [destinationId, form, isEditing, navigate, token]);

  const cleanList = (list, predicate) =>
    (Array.isArray(list) ? list : []).filter(predicate);

  const moveChildDestination = (dragIndex, hoverIndex) => {
    const currentChildDestinations = form.getFieldValue("child_destinations") || [];
    const draggedItem = currentChildDestinations[dragIndex];

    if (!draggedItem) {
      return;
    }

    form.setFieldValue(
      "child_destinations",
      update(currentChildDestinations, {
        $splice: [
          [dragIndex, 1],
          [hoverIndex, 0, draggedItem],
        ],
      })
    );
  };

  const searchHotels = (searchValue) => {
    const query = (searchValue || "").trim();
    latestHotelSearchRef.current = query;
    setHotelSearchQuery(query);
    setHotelSearchError("");

    if (hotelSearchTimeoutRef.current) {
      clearTimeout(hotelSearchTimeoutRef.current);
    }

    if (query.length < 2) {
      setHotelSearchOptions([]);
      setHotelSearchLoading(false);
      return;
    }

    hotelSearchTimeoutRef.current = setTimeout(async () => {
      setHotelSearchLoading(true);

      try {
        const res = await fetch(
          `${API_BASE_URL}destinationHotelLookup&query=${encodeURIComponent(query)}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        const result = await res.json();

        if (latestHotelSearchRef.current !== query) {
          return;
        }

        if (!res.ok || !result.success) {
          setHotelSearchOptions([]);
          setHotelSearchError(result.error || "Failed to load hotel options");
          return;
        }

        setHotelSearchOptions(
          (Array.isArray(result.data) ? result.data : []).map((hotel) => ({
            value: String(hotel.hotel_id),
            label: `${hotel.hotel_name} (${hotel.hotel_id})`,
            hotel_name: hotel.hotel_name,
          }))
        );
      } catch (error) {
        console.error(error);
        if (latestHotelSearchRef.current === query) {
          setHotelSearchOptions([]);
          setHotelSearchError("Failed to load hotel options");
        }
      } finally {
        if (latestHotelSearchRef.current === query) {
          setHotelSearchLoading(false);
        }
      }
    }, 300);
  };

  const buildRecommendedHotelOptions = (fieldName) => {
    const selectedHotelId = form.getFieldValue([
      "recommended_hotels",
      fieldName,
      "hotel_id",
    ]);
    const selectedHotelName = form.getFieldValue([
      "recommended_hotels",
      fieldName,
      "hotel_name",
    ]);

    const options = [...hotelSearchOptions];
    if (
      selectedHotelId &&
      !options.some((option) => String(option.value) === String(selectedHotelId))
    ) {
      options.unshift({
        value: String(selectedHotelId),
        label: selectedHotelName
          ? `${selectedHotelName} (${selectedHotelId})`
          : `Hotel ${selectedHotelId}`,
        hotel_name: selectedHotelName || "",
      });
    }

    return options;
  };

  const getHotelNotFoundContent = () => {
    if (hotelSearchLoading) {
      return "Searching...";
    }

    if (hotelSearchError) {
      return hotelSearchError;
    }

    if (hotelSearchQuery.length < 2) {
      return "Type at least 2 characters";
    }

    return "No matching hotels found";
  };

  const buildPayload = (values) => ({
    id: isEditing ? Number(destinationId) : undefined,
    type: values.type,
    status: values.status,
    parent_id: values.type === "city" ? values.parent_id || null : null,
    slug: values.slug?.trim() || "",
    hero_background_image_url: values.hero_background_image_url?.trim() || null,
    destination_images_json: cleanList(
      values.destination_images,
      (item) => item?.url?.trim() || item?.alt?.trim()
    ).map((item) => ({
      url: item.url.trim(),
      alt: item.alt?.trim() || "",
    })),
    child_destinations:
      values.type === "country"
        ? cleanList(
            values.child_destinations,
            (item) => item?.destination_id
          ).map((item, index) => ({
            destination_id: Number(item.destination_id),
            child_sort_order: index + 1,
          }))
        : [],
    recommended_hotels_json: cleanList(
      values.recommended_hotels,
      (item) =>
        item?.hotel_id || item?.hotel_name?.trim() || item?.cover_image_url?.trim()
    ).map((item) => ({
      hotel_id: item.hotel_id ? String(item.hotel_id) : null,
      hotel_name: item.hotel_name?.trim() || null,
      cover_image_url: item.cover_image_url?.trim() || "",
    })),
    translations: (values.translations || []).map((translation) => ({
      locale: translation.locale,
      name: translation.name?.trim() || "",
      page_title: translation.page_title?.trim() || "",
      meta_description: translation.meta_description?.trim() || "",
      hero_title: translation.hero_title?.trim() || "",
      hero_subtitle: translation.hero_subtitle?.trim() || "",
      parent_card_intro:
        values.type === "city"
          ? translation.parent_card_intro?.trim() || null
          : null,
      tabs_json: cleanList(
        translation.tabs,
        (tab) =>
          tab?.label?.trim() ||
          tab?.content_text?.trim() ||
          (tab?.content_items || []).some(
            (item) => item?.label?.trim() || item?.value?.trim()
          )
      ).map((tab) => ({
        label: tab.label?.trim() || "",
        type: tab.type || "plain_text",
        content:
          tab.type === "structured_list"
            ? cleanList(
                tab.content_items,
                (item) => item?.label?.trim() || item?.value?.trim()
              ).map((item) => ({
                label: item.label?.trim() || "",
                value: item.value?.trim() || "",
              }))
            : tab.content_text?.trim() || "",
      })),
      section_title: translation.section_title?.trim() || null,
      things_to_do_json: cleanList(
        values.type === "city" ? translation.things_to_do : [],
        (item) =>
          item?.title?.trim() ||
          item?.description?.trim() ||
          item?.image_url?.trim()
      ).map((item) => ({
        title: item.title?.trim() || "",
        description: item.description?.trim() || "",
        image_url: item.image_url?.trim() || "",
      })),
      packages_title: translation.packages_title?.trim() || "",
      packages_description: translation.packages_description?.trim() || "",
      featured_resorts_title: translation.featured_resorts_title?.trim() || "",
      faq_title: translation.faq_title?.trim() || "",
      faqs_json: cleanList(
        translation.faqs,
        (item) => item?.question?.trim() || item?.answer?.trim()
      ).map((item) => ({
        question: item.question?.trim() || "",
        answer: item.answer?.trim() || "",
      })),
    })),
  });

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      const payload = buildPayload(values);

      setSaving(true);
      const res = await fetch(`${API_BASE_URL}destinations`, {
        method: isEditing ? "PUT" : "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });
      const result = await res.json();

      if (res.ok && result.success) {
        message.success(
          isEditing ? "Destination updated" : "Destination created"
        );
        navigate("/destinations");
      } else {
        message.error(result.error || "Failed to save destination");
      }
    } catch (error) {
      if (error?.errorFields) {
        const firstErrorField = error.errorFields[0]?.name;
        if (firstErrorField) {
          form.scrollToField(firstErrorField, {
            behavior: "smooth",
            block: "center",
          });
        }
        message.error("Please complete the required fields before saving");
        return;
      }

      console.error(error);
      message.error("Failed to save destination");
    } finally {
      setSaving(false);
    }
  };

  const parentOptions = destinations
    .filter((item) => item.type === "country" && String(item.id) !== destinationId)
    .map((item) => ({
      label: item.display_name || `#${item.id}`,
      value: Number(item.id),
    }));

  if (pageLoading) {
    return (
      <div style={{ padding: "80px 0", textAlign: "center" }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Space direction="vertical" size={4}>
            <Button
              type="link"
              icon={<ArrowLeftOutlined />}
              onClick={() => navigate("/destinations")}
              style={{ paddingLeft: 0 }}
            >
              Back to Destination Management
            </Button>
            <Title level={2} style={{ margin: 0 }}>
              {isEditing
                ? `Edit Destination #${destinationId}`
                : "Create Destination"}
            </Title>
            <Text type="secondary">
              Manage shared destination fields and locale-specific content on a
              dedicated page.
            </Text>
          </Space>
        </Col>
      </Row>

      <Form form={form} layout="vertical">
        <Card title="Shared Destination Data" style={{ marginBottom: 16 }}>
          <Row gutter={16}>
            <Col xs={24} md={8}>
              <Form.Item
                label="Type"
                name="type"
                rules={[{ required: true, message: "Please select a type" }]}
              >
                <Select options={TYPE_OPTIONS} />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item
                label="Status"
                name="status"
                rules={[{ required: true, message: "Please select a status" }]}
              >
                <Select options={STATUS_OPTIONS} />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item
                label="Parent Destination"
                name="parent_id"
                rules={
                  selectedType === "city"
                    ? [
                        {
                          required: true,
                          message: "Please select a parent country",
                        },
                      ]
                    : []
                }
              >
                <Select
                  allowClear
                  disabled={selectedType !== "city"}
                  options={parentOptions}
                  placeholder={
                    selectedType === "city"
                      ? "Select parent country"
                      : "Only used for cities"
                  }
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item
                label="Slug"
                name="slug"
                rules={[{ required: true, message: "Please enter a slug" }]}
              >
                <Input placeholder="mexico" />
              </Form.Item>
            </Col>
          </Row>

          <DestinationImageField
            form={form}
            name="hero_background_image_url"
            label="Hero Background Image URL"
            token={token}
            category="hero-background"
            previewAlt="Hero background"
            previewHeight={180}
          />

          <Form.List name="destination_images">
            {(fields, { add, remove }) => (
              <Card
                type="inner"
                title="Destination Images"
                style={{ marginBottom: 16 }}
              >
                {fields.length === 0 ? (
                  <Text type="secondary" style={{ display: "block", marginBottom: 16 }}>
                    No destination images added yet.
                  </Text>
                ) : null}

                {fields.map((field, index) => (
                  <Card
                    key={field.key}
                    type="inner"
                    size="small"
                    title={`Image ${index + 1}`}
                    style={{ marginBottom: 16 }}
                    extra={
                      <Button
                        danger
                        type="text"
                        icon={<DeleteOutlined />}
                        onClick={() => remove(field.name)}
                      >
                        Remove
                      </Button>
                    }
                  >
                    <Row gutter={16} align="top">
                      <Col xs={24} md={10}>
                        <DestinationImageField
                          form={form}
                          name={["destination_images", field.name, "url"]}
                          label="Image"
                          token={token}
                          category="destination-gallery"
                          previewAlt={`Destination image ${index + 1}`}
                          previewHeight={160}
                        />
                      </Col>
                      <Col xs={24} md={14}>
                        <Form.Item
                          label="Alt Text For This Image"
                          name={[field.name, "alt"]}
                        >
                          <Input placeholder="Describe this image" />
                        </Form.Item>
                      </Col>
                    </Row>
                  </Card>
                ))}

                <AddActionButton onClick={() => add(buildEmptyImage())}>
                  Add Image
                </AddActionButton>
              </Card>
            )}
          </Form.List>

          {selectedType === "country" && (
            <DndProvider backend={HTML5Backend}>
              <Form.List name="child_destinations">
                {(fields) => (
                  <Card
                    type="inner"
                    title="Child Destinations"
                    style={{ marginBottom: 16 }}
                  >
                    <Text
                      type="secondary"
                      style={{ display: "block", marginBottom: 16 }}
                    >
                      Child destinations are pulled automatically from city
                      records assigned to this country. Drag rows to change the
                      display order. The preview uses each child destination&apos;s
                      hero image.
                    </Text>
                    {fields.length === 0 ? (
                      <Text type="secondary">
                        {isEditing
                          ? "No city destinations are currently assigned to this country."
                          : "Child destinations will appear here after city records are created and assigned to this country."}
                      </Text>
                    ) : (
                      fields.map((field, index) => (
                        <DraggableChildDestinationRow
                          key={field.key}
                          index={index}
                          moveChildDestination={moveChildDestination}
                        >
                          {(dragHandleRef) => (
                            <Row gutter={12} align="middle">
                              <Col xs={24} md={1}>
                                <div
                                  ref={dragHandleRef}
                                  style={{
                                    cursor: "move",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    height: "100%",
                                    color: "#8c8c8c",
                                  }}
                                >
                                  <HolderOutlined />
                                </div>
                              </Col>
                              <Col xs={24} md={3}>
                                <Form.Item label="Order">
                                  <Input value={index + 1} disabled />
                                </Form.Item>
                              </Col>
                              <Col xs={24} md={6}>
                                <Form.Item
                                  label="Destination"
                                  name={[field.name, "destination_name"]}
                                >
                                  <Input disabled />
                                </Form.Item>
                                <Form.Item
                                  name={[field.name, "destination_id"]}
                                  hidden
                                >
                                  <Input />
                                </Form.Item>
                              </Col>
                              <Col xs={24} md={8}>
                                <Text type="secondary">Hero Image</Text>
                                <Form.Item noStyle shouldUpdate>
                                  {() => (
                                    <ImageUrlPreview
                                      url={form.getFieldValue([
                                        "child_destinations",
                                        field.name,
                                        "hero_background_image_url",
                                      ])}
                                      alt="Child destination"
                                      height={110}
                                    />
                                  )}
                                </Form.Item>
                              </Col>
                              <Form.Item
                                name={[field.name, "hero_background_image_url"]}
                                hidden
                              >
                                <Input />
                              </Form.Item>
                            </Row>
                          )}
                        </DraggableChildDestinationRow>
                      ))
                    )}
                  </Card>
                )}
              </Form.List>
            </DndProvider>
          )}

          <Form.List name="recommended_hotels">
            {(fields, { add, remove }) => (
              <Card type="inner" title="Recommended Hotels">
                  {fields.length === 0 ? (
                    <Text type="secondary" style={{ display: "block", marginBottom: 16 }}>
                      No recommended hotels added yet.
                    </Text>
                  ) : null}

                  {fields.map((field, index) => (
                    <Card
                      key={field.key}
                      type="inner"
                      size="small"
                      title={`Hotel ${index + 1}`}
                      style={{ marginBottom: 16 }}
                      extra={
                        <Button
                          danger
                          type="text"
                          icon={<DeleteOutlined />}
                          onClick={() => remove(field.name)}
                        >
                          Remove
                        </Button>
                      }
                    >
                      <Row gutter={16} align="top">
                        <Col xs={24} md={12}>
                          <Form.Item
                            label="Hotel"
                            name={[field.name, "hotel_id"]}
                            rules={[
                              {
                                required: true,
                                message: "Please select a hotel",
                              },
                            ]}
                          >
                            <Select
                              showSearch
                              allowClear
                              filterOption={false}
                              placeholder="Type part of a hotel name"
                              notFoundContent={getHotelNotFoundContent()}
                              options={buildRecommendedHotelOptions(field.name)}
                              onSearch={searchHotels}
                              onSelect={(_, option) => {
                                form.setFieldValue(
                                  ["recommended_hotels", field.name, "hotel_name"],
                                  option?.hotel_name || ""
                                );
                              }}
                              onClear={() => {
                                form.setFieldValue(
                                  ["recommended_hotels", field.name, "hotel_name"],
                                  ""
                                );
                              }}
                              loading={hotelSearchLoading}
                            />
                          </Form.Item>
                          <Form.Item name={[field.name, "hotel_name"]} hidden>
                            <Input />
                          </Form.Item>
                        </Col>
                        <Col xs={24} md={12}>
                          <DestinationImageField
                            form={form}
                            name={[
                              "recommended_hotels",
                              field.name,
                              "cover_image_url",
                            ]}
                            label="Cover Image"
                            token={token}
                            category="recommended-hotels"
                            previewAlt={`Recommended hotel ${index + 1}`}
                            previewHeight={160}
                          />
                        </Col>
                      </Row>
                    </Card>
                  ))}
                  <AddActionButton onClick={() => add(buildEmptyHotel())}>
                    Add Hotel
                  </AddActionButton>
              </Card>
            )}
          </Form.List>
        </Card>

        <Form.List name="translations">
          {(translationFields, { add, remove }) => (
            <Card
              title="Translations"
              extra={
                <AddActionButton onClick={() => add(buildEmptyTranslation())}>
                  Add Translation
                </AddActionButton>
              }
            >
              {translationFields.map((translationField, index) => (
                <Card
                  key={translationField.key}
                  type="inner"
                  title={`Translation ${index + 1}`}
                  style={{ marginBottom: 16 }}
                  extra={
                    translationFields.length > 1 ? (
                      <Button
                        danger
                        type="link"
                        onClick={() => remove(translationField.name)}
                      >
                        Remove
                      </Button>
                    ) : null
                  }
                >
                  <Row gutter={16}>
                    <Col xs={24} md={8}>
                      <Form.Item
                        label="Locale"
                        name={[translationField.name, "locale"]}
                        rules={[
                          {
                            required: true,
                            message: "Please select a locale",
                          },
                        ]}
                      >
                        <Select options={LOCALE_OPTIONS} />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={8}>
                      <Form.Item
                        label="Name"
                        name={[translationField.name, "name"]}
                        rules={[
                          { required: true, message: "Please enter a name" },
                        ]}
                      >
                        <Input />
                      </Form.Item>
                    </Col>
                  </Row>

                  <Form.Item
                    label="Page Title"
                    name={[translationField.name, "page_title"]}
                    rules={[
                      {
                        required: true,
                        message: "Please enter a page title",
                      },
                    ]}
                  >
                    <Input />
                  </Form.Item>

                  <Form.Item
                    label="Meta Description"
                    name={[translationField.name, "meta_description"]}
                    rules={[
                      {
                        required: true,
                        message: "Please enter a meta description",
                      },
                    ]}
                  >
                    <TextArea rows={3} />
                  </Form.Item>

                  <Row gutter={16}>
                    <Col xs={24} md={12}>
                      <Form.Item
                        label="Hero Title"
                        name={[translationField.name, "hero_title"]}
                        rules={[
                          {
                            required: true,
                            message: "Please enter a hero title",
                          },
                        ]}
                      >
                        <Input />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={12}>
                      <Form.Item
                        label="Section Title"
                        name={[translationField.name, "section_title"]}
                      >
                        <Input />
                      </Form.Item>
                    </Col>
                  </Row>

                  <Form.Item
                    label="Hero Subtitle"
                    name={[translationField.name, "hero_subtitle"]}
                    rules={[
                      {
                        required: true,
                        message: "Please enter a hero subtitle",
                      },
                    ]}
                  >
                    <TextArea rows={3} />
                  </Form.Item>

                  {selectedType === "city" ? (
                    <Form.Item
                      label="Parent Country Card Intro"
                      name={[translationField.name, "parent_card_intro"]}
                    >
                      <TextArea rows={2} placeholder="Short sentence shown on the parent country page card" />
                    </Form.Item>
                  ) : null}

                  <Form.List name={[translationField.name, "tabs"]}>
                    {(tabFields, { add: addTab, remove: removeTab }) => (
                      <Card
                        type="inner"
                        title="Tabs"
                        style={{ marginBottom: 16 }}
                        extra={
                          <AddActionButton onClick={() => addTab(buildEmptyTab())}>
                            Add Tab
                          </AddActionButton>
                        }
                      >
                        {tabFields.map((tabField) => (
                          <TranslationTabEditor
                            key={tabField.key}
                            form={form}
                            translationIndex={translationField.name}
                            tabField={tabField}
                            removeTab={removeTab}
                          />
                        ))}
                      </Card>
                    )}
                  </Form.List>

                  {selectedType === "city" ? (
                    <Form.List name={[translationField.name, "things_to_do"]}>
                      {(thingFields, { add: addThing, remove: removeThing }) => (
                        <Card
                          type="inner"
                          title="Things To Do"
                          style={{ marginBottom: 16 }}
                          extra={
                            <AddActionButton onClick={() => addThing(buildEmptyThingToDo())}>
                              Add Item
                            </AddActionButton>
                          }
                        >
                            {thingFields.map((thingField) => (
                              <Card
                                key={thingField.key}
                              type="inner"
                              size="small"
                              style={{ marginBottom: 12 }}
                              extra={
                                <Button
                                  danger
                                  type="text"
                                  icon={<DeleteOutlined />}
                                  onClick={() => removeThing(thingField.name)}
                                />
                              }
                              >
                                <Row gutter={12}>
                                <Col xs={24} md={8}>
                                  <Form.Item
                                    label="Title"
                                    name={[thingField.name, "title"]}
                                  >
                                    <Input />
                                  </Form.Item>
                                </Col>
                                <Col xs={24} md={10}>
                                  <DestinationImageField
                                    form={form}
                                    name={[
                                      "translations",
                                      translationField.name,
                                      "things_to_do",
                                      thingField.name,
                                      "image_url",
                                    ]}
                                    label="Image URL"
                                    token={token}
                                    category="things-to-do"
                                    previewAlt="Things to do"
                                    previewHeight={110}
                                  />
                                </Col>
                                </Row>
                              <Form.Item
                                label="Description"
                                name={[thingField.name, "description"]}
                              >
                                <TextArea rows={3} />
                              </Form.Item>
                            </Card>
                          ))}
                        </Card>
                      )}
                    </Form.List>
                  ) : null}

                  <Row gutter={16}>
                    <Col xs={24} md={12}>
                      <Form.Item
                        label="Packages Title"
                        name={[translationField.name, "packages_title"]}
                        rules={[
                          {
                            required: true,
                            message: "Please enter packages title",
                          },
                        ]}
                      >
                        <Input />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={12}>
                      <Form.Item
                        label="Featured Resorts Title"
                        name={[translationField.name, "featured_resorts_title"]}
                        rules={[
                          {
                            required: true,
                            message: "Please enter featured resorts title",
                          },
                        ]}
                      >
                        <Input />
                      </Form.Item>
                    </Col>
                  </Row>

                  <Form.Item
                    label="Packages Description"
                    name={[translationField.name, "packages_description"]}
                    rules={[
                      {
                        required: true,
                        message: "Please enter packages description",
                      },
                    ]}
                  >
                    <TextArea rows={3} />
                  </Form.Item>

                  <Form.Item
                    label="FAQ Title"
                    name={[translationField.name, "faq_title"]}
                    rules={[
                      { required: true, message: "Please enter FAQ title" },
                    ]}
                  >
                    <Input />
                  </Form.Item>

                  <Form.List name={[translationField.name, "faqs"]}>
                    {(faqFields, { add: addFaq, remove: removeFaq }) => (
                      <Card
                        type="inner"
                        title="FAQs"
                        style={{ marginBottom: 16 }}
                        extra={
                          <AddActionButton onClick={() => addFaq(buildEmptyFaq())}>
                            Add FAQ
                          </AddActionButton>
                        }
                      >
                        {faqFields.map((faqField) => (
                          <Card
                            key={faqField.key}
                            type="inner"
                            size="small"
                            style={{ marginBottom: 12 }}
                            extra={
                              <Button
                                danger
                                type="text"
                                icon={<DeleteOutlined />}
                                onClick={() => removeFaq(faqField.name)}
                              />
                            }
                          >
                            <Form.Item
                              label="Question"
                              name={[faqField.name, "question"]}
                              rules={[
                                {
                                  required: true,
                                  message: "Please enter a question",
                                },
                              ]}
                            >
                              <Input />
                            </Form.Item>
                            <Form.Item
                              label="Answer"
                              name={[faqField.name, "answer"]}
                              rules={[
                                {
                                  required: true,
                                  message: "Please enter an answer",
                                },
                              ]}
                            >
                              <TextArea rows={3} />
                            </Form.Item>
                          </Card>
                        ))}
                      </Card>
                    )}
                  </Form.List>

                </Card>
              ))}
            </Card>
          )}
        </Form.List>
      </Form>

      <div
        style={{
          position: "fixed",
          right: 32,
          bottom: 24,
          zIndex: 1000,
          background: "rgba(255, 255, 255, 0.96)",
          border: "1px solid #f0f0f0",
          borderRadius: 12,
          boxShadow: "0 8px 24px rgba(0, 0, 0, 0.12)",
          padding: 12,
          backdropFilter: "blur(8px)",
        }}
      >
        <Button type="primary" loading={saving} onClick={handleSubmit}>
          Save Destination
        </Button>
      </div>
    </div>
  );
};

export default DestinationEditorPage;
